import styled from 'styled-components';

import { media } from 'src/styles/breakpoints';

export const TransactionTabs = styled.nav`
  display: flex;
  flex-wrap: nowrap;
  gap: 1.5rem;
  margin: 0 0 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TransactionTab = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  position: relative;
  padding: 0.75rem 0;
  border: none;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.dark : theme.midGrey)};
  font-size: 1.0625rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({ theme, $active }) => ($active ? theme.dark : 'transparent')};
  }

  &:hover {
    color: ${({ theme }) => theme.dark};
    opacity: 0.85;
  }
`;

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

export const ActionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;

  ${media.md} {
    flex-direction: column;
  }
`;

export const NotesInput = styled.textarea`
  width: 100%;
  min-height: 5.5rem;
  margin-top: 0.35rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.625rem;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.dark};
  font: inherit;
  font-size: 0.875rem;
  resize: vertical;

  &:disabled {
    opacity: 0.65;
  }
`;

export const ActivityList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ActivityItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

export const Breadcrumb = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
  margin-bottom: 0.75rem;
`;

export const HeadlineRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const Title = styled.h2`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.625rem;
  margin: 0;
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.dark};
`;

export const MetaLine = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  text-transform: lowercase;
  background: ${({ $status, theme }) => {
    const s = $status.toLowerCase().replace(/_/g, ' ');
    if (
      [
        'completed',
        'complete',
        'success',
        'fulfilled',
        'sent',
        'verified',
        'low risk',
        'low',
        'active',
        'released',
        'cleared',
        'resolved',
      ].includes(s)
    ) {
      return theme.positive90;
    }
    if (
      [
        'failed',
        'rejected',
        'cancelled',
        'canceled',
        'not sent',
        'high risk',
        'high',
        'prohibited',
        'severe',
        'unverified',
        'confirmed',
      ].includes(s)
    ) {
      return theme.negative90;
    }
    if (
      [
        'pending',
        'processing',
        'in progress',
        'in_progress',
        'held',
        'frozen',
        'under review',
        'under_review',
        'approval pending',
        'open',
        'medium',
        'escalated',
      ].includes(s)
    ) {
      return theme.middle90;
    }
    return theme.lightGrey;
  }};
  color: ${({ $status, theme }) => {
    const s = $status.toLowerCase().replace(/_/g, ' ');
    if (
      [
        'completed',
        'complete',
        'success',
        'fulfilled',
        'sent',
        'verified',
        'low risk',
        'low',
        'active',
        'released',
        'cleared',
        'resolved',
      ].includes(s)
    ) {
      return theme.positive;
    }
    if (
      [
        'failed',
        'rejected',
        'cancelled',
        'canceled',
        'not sent',
        'high risk',
        'high',
        'prohibited',
        'severe',
        'unverified',
        'confirmed',
      ].includes(s)
    ) {
      return theme.negative;
    }
    if (
      [
        'pending',
        'processing',
        'in progress',
        'in_progress',
        'held',
        'frozen',
        'under review',
        'under_review',
        'approval pending',
        'open',
        'medium',
        'escalated',
      ].includes(s)
    ) {
      return theme.middle;
    }
    return theme.midGrey;
  }};

  &::first-letter {
    text-transform: uppercase;
  }
`;

export const Card = styled.section`
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.875rem;
  padding: 1.25rem;
  background: ${({ theme }) => theme.background};
`;

export const CardTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dark};
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const DetailLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
`;

export const DetailValue = styled.div<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme, $muted }) => ($muted ? theme.midGrey : theme.dark)};
  word-break: break-word;
`;

export const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.midGrey};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.dark};
  }
`;

export const UserLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: ${({ theme }) => theme.primary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;
